/**
 * Polatlar Mobilya - Türkçe Para Formatı Yardımcıları
 *
 * formatCurrencyInput: ham sayısal string'i görüntülenebilir TR formatına çevirir
 *   "80000"     → "80.000"
 *   "1250000"   → "1.250.000"
 *   "10500.50"  → "10.500,50"
 *   "10500,50"  → "10.500,50"
 *
 * parseCurrencyInput: görüntülenen TR formatını ham sayısal string'e çevirir
 *   "80.000"       → "80000"
 *   "1.250.000"    → "1250000"
 *   "10.500,50"    → "10500.50"
 *   "80.000,50"    → "80000.50"
 *
 * formatTRY: sayıyı Türkçe para birimi gösterimine çevirir
 *   80000 → "₺80.000"
 *   1250000 → "₺1.250.000"
 */

/**
 * Ham sayısal değeri görüntü formatına çevir (noktalı binlik, virgüllü ondalık)
 * Girdi: "80000", "10500.50", "10500,50", "80.000", "10.500,50"
 * Çıktı: "80.000", "10.500,50", "10.500,50"
 */
export function formatCurrencyInput(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';

  // Number tipi: doğrudan Intl.NumberFormat ile formatla
  if (typeof value === 'number') {
    const hasDecimal = Math.floor(value) !== value;
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: hasDecimal ? 2 : 0,
      maximumFractionDigits: 2,
    }).format(value);
  }

  let str = String(value).trim();
  if (!str) return '';

  // Ham string'i parseCurrencyInput ile normalize et, sonra formatla
  const raw = parseCurrencyInput(str);
  if (!raw) return '';

  const num = parseFloat(raw);
  if (isNaN(num)) return '';

  const hasDecimal = raw.includes('.');
  return new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits: hasDecimal ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(num);
}

/**
 * Görüntü formatındaki değeri ham sayısal metne çevir
 * "80.000"       → "80000"
 * "10.500,50"    → "10500.50"
 * "1.250.000"    → "1250000"
 * "₺80.000"      → "80000"
 * "5000.00"      → "5000.00"  (toFixed string, nokta = ondalık)
 * "10.50"        → "10.50"    (nokta = ondalık)
 * "10.500"       → "10500"    (nokta = binlik)
 */
export function parseCurrencyInput(value: string): string {
  if (!value || !value.trim()) return '';

  let cleaned = value.trim();

  // ₺ sembolünü ve boşlukları temizle
  cleaned = cleaned.replace(/[₺TL\s]/gi, '');

  // Eğer hiç rakam yoksa boş dön
  if (!/\d/.test(cleaned)) return '';

  const hasComma = cleaned.includes(',');

  if (hasComma) {
    // Virgül varsa: noktalar binlik ayracı, virgül ondalık ayracı
    cleaned = cleaned.replace(/\./g, '').replace(',', '.');
  } else {
    // Virgül yok, nokta varsa
    const parts = cleaned.split('.');
    if (parts.length === 2 && parts[1].length <= 2 && /^\d+$/.test(parts[1]) && parts[0].length > 0) {
      // Tek nokta, sonrası 1-2 basamak, öncesi var → ondalık ayracı (örn: 5000.00, 10500.50, 10.5)
      // Değişiklik yok
    } else {
      // Birden fazla nokta veya sonrası 3+ basamak → binlik ayracı
      cleaned = cleaned.replace(/\./g, '');
    }
  }

  // Baştaki sıfırları temizle (ama "0.50" gibi durumları koru)
  if (cleaned.includes('.')) {
    const [intPart, decPart] = cleaned.split('.');
    const trimmedInt = intPart.replace(/^0+/, '') || '0';
    cleaned = trimmedInt + '.' + decPart;
  } else {
    cleaned = cleaned.replace(/^0+/, '') || '0';
  }

  return cleaned;
}

export interface FormatTRYOptions {
  /** ₺ simgesi gösterilsin mi (varsayılan: true) */
  showSymbol?: boolean;
  /** Simge konumu: 'prefix' (₺80.000) veya 'suffix' (80.000 ₺), varsayılan 'prefix' */
  symbolPosition?: 'prefix' | 'suffix';
  /** Minimum ondalık basamak sayısı (varsayılan: 0) */
  minFractionDigits?: number;
  /** Maksimum ondalık basamak sayısı (varsayılan: 2) */
  maxFractionDigits?: number;
}

/**
 * Sayıyı Türkçe para birimi formatında göster
 * 80000 → "₺80.000"
 * 1250000,50 → "₺1.250.000,50"
 *
 * @param amount - formatlanacak sayı
 * @param options - format seçenekleri
 */
export function formatTRY(
  amount: number | null | undefined,
  options: FormatTRYOptions = {},
): string {
  const {
    showSymbol = true,
    symbolPosition = 'prefix',
    minFractionDigits = 0,
    maxFractionDigits = 2,
  } = options;

  if (amount === null || amount === undefined) return showSymbol ? '₺0' : '0';
  if (isNaN(amount)) return showSymbol ? '₺0' : '0';

  const absAmount = Math.abs(amount);
  const formatted = new Intl.NumberFormat('tr-TR', {
    style: 'decimal',
    minimumFractionDigits: minFractionDigits,
    maximumFractionDigits: maxFractionDigits,
  }).format(absAmount);

  const sign = amount < 0 ? '-' : '';

  if (!showSymbol) return sign + formatted;

  if (symbolPosition === 'prefix') {
    return sign + '₺' + formatted;
  }
  return sign + formatted + ' ₺';
}